import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationState } from '../../classes/ApplicationState';
import { AmorphicService } from '../amorphic/amorphic.service';
import { ValidationService } from '../validation/validation.service';
import { FormService } from '../form/form.service';
import { ParserService } from '../../../../common/angular-shared/services/parser/parser.service';
import { WindowService } from '../../../../common/angular-shared/services/window/window.service';
import { FormatterService } from '../formatter/formatter.service';
import { QuoteService } from '../quote/quote.service';
import { ValidationResult } from '../../interfaces/validation/ValidationInterface';
import { CasMetadataNode, DiagnosisShortNode } from '../../classes/CasMetadataNode';
import { BaseQuote } from '../../../../common/js/quote/BaseQuote';
import { Observable, Subject } from 'rxjs';
import { ShareLinkService } from '../../../../coverpath-agent/public/js/services/customer-interaction-service/share-link.service';
import { AccountCenterService } from '../account-center/account-center.service';
import * as ld from 'lodash';

import { ModalService } from '../modal/modal.service';
import { CoverpathURLs, LockedByUserType, ModalType } from '../../../js/interfaces/CoverpathTypes';
import {
  AdvisorProcessingApplication,
  AdvisorSubmitFailedConfig,
} from '../../components/modals/partials/generic/generic.configs';
import { SubjectManager } from '../../../../common/js/subjectManager/SubjectManager';
import { AgentController, InviteEmailType } from '../../../../coverpath-agent/public/js/types/Types';
import { Product, PropertiesForECP, PropertiesForWoP } from '../../../../common/js/static/Product';
import { AMCAuth0Service } from '../authentication/amc/amc-auth0.service';
import { CasApplication } from '../../../classes/coverpath_subclasses/CasApplication';
import { ApplicationDataService } from '../../../js/static/ApplicationDataService';
import { CoverpathBasePolicy } from '../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { CoverpathPolicy } from '../../../classes/coverpath_subclasses/CoverpathPolicy';
import { MMDirectPolicy } from '../../../classes/mmdirect_subclasses/MMDirectPolicy';
import { CoverpathCustomer } from '../../../classes/coverpath_subclasses/CoverpathCustomer';
import { ObfuscationProperty } from '../../../../common/js/interfaces/CommonTypes';
import { UICategory, UILoggerSingleton } from '../../../../common/js/logger/UILoggerSingleton';
import { Application } from '../../../../common/js/customer/Application';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService extends FormService {
  appState: ApplicationState;
  previouslyNonICC: boolean = false;
  initialPrimaryCustomerState: string;
  questionAnsweredSubject: Subject<void>;
  questionAnsweredObservable$: Observable<void>;
  private previousPolicyState: string;
  private previousPolicyICCStatus: boolean;
  sendButtonDisabledSubject: Subject<boolean>;
  sendButtonDisabledObservable$: Observable<boolean>;
  isDisabledSubject: Subject<void>;
  isDisabledObservable$: Observable<void>;
  advisorSubmittedApplicationSentSubject: Subject<boolean>;
  advisorSubmittedApplicationSentObservable$: Observable<boolean>;
  loadingSubject: Subject<boolean>;
  missedQuestionSubject: Subject<void>;
  teleMissedQuestionSubject: Subject<void>;
  obfuscatePiiSubject: Subject<ObfuscationProperty>;
  obfuscatePiiObservable$: Observable<ObfuscationProperty>;

  constructor(
    protected amorphicService: AmorphicService,
    protected parserService: ParserService,
    protected validationService: ValidationService,
    protected formatterService: FormatterService,
    protected windowService: WindowService,
    protected router: Router,
    protected injector: Injector,
    protected quoteService: QuoteService,
    protected auth0Service: AMCAuth0Service
  ) {
    super(amorphicService, parserService, formatterService, validationService);
    this.appState = new ApplicationState(windowService);
    this.questionAnsweredSubject = new Subject<void>();
    this.questionAnsweredObservable$ = this.questionAnsweredSubject.asObservable();
    this.sendButtonDisabledSubject = new Subject<boolean>();
    this.loadingSubject = new Subject<boolean>();
    this.sendButtonDisabledObservable$ = this.sendButtonDisabledSubject.asObservable();
    this.advisorSubmittedApplicationSentSubject = new Subject<boolean>();
    this.advisorSubmittedApplicationSentObservable$ = this.advisorSubmittedApplicationSentSubject.asObservable();
    this.missedQuestionSubject = new Subject<void>();
    this.isDisabledSubject = new Subject<void>();
    this.isDisabledObservable$ = this.isDisabledSubject.asObservable();
    this.teleMissedQuestionSubject = new Subject<void>();

    this.obfuscatePiiSubject = new Subject<ObfuscationProperty>();
    this.obfuscatePiiObservable$ = this.obfuscatePiiSubject.asObservable();
  }

  public async resolveApplicationData() {
    const metadata = await this.controller.publicFetchMetaData();

    /**
     * if Coverpath OR MMD policy
     * AND insured is juvenile
     * AND is not agent assisted app OR unemploymentDuration is undefined
     * then it is the first time this application is being loaded
     * so prefill answers for 2019 metadata
     */
    const application = this.controller.customer.application;
    const primaryPolicy = application.primaryPolicy;
    if (
      (primaryPolicy.isCoverpathPolicy() || primaryPolicy.isMMDirectPolicy()) &&
      primaryPolicy.insuredIsJuvenile() &&
      (!application.isAgentAssistant || primaryPolicy.insured.financials.unemploymentDuration === undefined)
    ) {
      primaryPolicy.prefillJuvenileQuestions();
    }

    await this.resolveTimeSensitiveAppData(application);

    if (metadata && metadata['App_Questions'].length) {
      return this.appState.buildApplication(metadata);
    }
  }

  async resolveTimeSensitiveAppData(application: Application) {
    const primaryPolicy = application.primaryPolicy.isCoverpathPolicy()
      ? (application.primaryPolicy as CoverpathPolicy)
      : null;
    const secondaryPolicy = application.isJointApp() ? (application.secondaryPolicy as CoverpathPolicy) : null;
    if (primaryPolicy && !primaryPolicy.isWopEligibleForPrimaryPolicy() && primaryPolicy.waiver) {
      primaryPolicy.waiver = false;
      await ApplicationDataService.updateWOPAndPremium(this.controller, primaryPolicy);
    }
    if (secondaryPolicy && !secondaryPolicy.isWopEligibleForSecondaryPolicy() && secondaryPolicy.waiver) {
      secondaryPolicy.waiver = false;
      await ApplicationDataService.updateWOPAndPremium(this.controller, secondaryPolicy);
    }
  }

  // Provided by server implementation of ApplicationService
  getMetadata(md) {}

  removeIterateElement(prop, ix, removeFuncSuffix) {
    // Necessary to evaluate property for iterate element removal
    //tslint:disable-next-line
    let propCollection = eval('this.controller.' + prop);
    const element = propCollection[ix];

    if (removeFuncSuffix) {
      //tslint:disable-next-line
      eval('this.controller.' + prop + 'Remove(' + ix + ')');
    } else {
      propCollection.splice(ix, 1);
    }
    this.saveChanges();
  }

  addIterateElement(prop, arrayType, addFuncSuffix) {
    // Currently necessary for the evaluation of front end bindings
    //tslint:disable-next-line
    if (addFuncSuffix) {
      //tslint:disable-next-line
      eval('this.controller.' + prop + 'Add()');
    } else {
      //tslint:disable-next-line
      let propCollection = eval('this.controller.' + prop);

      const newElement = this.controller.applicationController.createIterateClass(arrayType, this.controller.customer);
      propCollection.push(newElement);
    }
    this.saveChanges();
  }

  getObjectType(property: string): string {
    const model = this.getModelFor(property);
    const propString = this.getPropStringFor(property);
    let propertyDef = null;
    let propertyType = null;

    if (model) {
      propertyDef = model.__prop__(propString);
      if (!propertyDef) {
        propertyDef = model[propString];
      }

      const tokens = propertyDef.type.toString().match(/function (.*)\(/);

      if (tokens) {
        propertyType = tokens[1];
      }
    } else {
      // placeholder until diagnosis short is fixed
      //tslint:disable-next-line
      console.log("-- TODO: Unable to getRulesForProperty('" + property + "')");
      propertyType = 'String';
    }

    return propertyType;
  }

  getRulesForProperty(property: string): Array<string> {
    let rulesArray = [];

    const model = this.getModelFor(property);
    const propString = this.getPropStringFor(property);
    let propertyDef = null;

    if (model) {
      propertyDef = model.__prop__(propString);
    } else {
      // placeholder until diagnosis short is fixed
      //tslint:disable-next-line
      console.log("-- TODO: Unable to getRulesForProperty('" + property + "')");
    }

    if (propertyDef) {
      if (propertyDef.rule) {
        rulesArray = Array.isArray(propertyDef.rule) ? propertyDef.rule : [propertyDef.rule];
      }
    }

    return rulesArray;
  }
  getResetOnError(property: string): boolean {
    const model = this.getModelFor(property);
    const propString = this.getPropStringFor(property);
    let propertyDef = null;
    if (model) {
      propertyDef = model.__prop__(propString);
    } else {
      // placeholder until diagnosis short is fixed
      //tslint:disable-next-line
      console.log(`-- TODO: Unable to getResetOnError('${property}')`);
    }
    if (propertyDef) {
      if (propertyDef.resetOnError) {
        return true;
      }
    }
    return false;
  }

  getFirstTypeRuleFromRules(rules: Array<string>): string {
    let firstRule = null;
    if (rules && rules.length > 0) {
      for (let i = 0; i < rules.length; i++) {
        firstRule = rules[i];
        if (firstRule && firstRule !== 'required') {
          break;
        }
      }
    }

    return firstRule;
  }

  isRequiredOrNotEmptyProperty(property: string, questionItem: CasMetadataNode): boolean {
    if (questionItem.optional) {
      return false;
    }
    const model = this.getModelFor(property);
    const propString = this.getPropStringFor(property);
    const propertyDef = model.__prop__(propString);
    const propDefValidatorStr = (propertyDef && propertyDef.validate) || '';
    const metadataValidatorStr = questionItem.validation || '';
    return (
      propDefValidatorStr.includes('notEmpty()') ||
      metadataValidatorStr.includes('notEmpty()') ||
      this.getRulesForProperty(property).includes('required')
    );
  }

  async runBusinessValidation(property, value, metadataValidation?: string, options?: {}) {
    const model = this.getModelFor(property);
    const propString = this.getPropStringFor(property);

    // First run the rules followed by validators
    // that have been setup on the data model
    const propertyDef = model.__prop__(propString);
    let result: ValidationResult = { result: true };

    if (propertyDef) {
      const rules = propertyDef.rule;
      let rulesArr = [];
      if (rules) {
        rulesArr = Array.isArray(rules) ? rules : [rules];
      }
      let isValid: boolean = rulesArr.every((rule) => {
        // Keep going until the first failure
        result = this.validationService.runRule(rule, value, options);
        return result.result;
      });

      if (isValid) {
        let validatorStr = propertyDef.validate;
        /**
         * Runs
         * 1) validate in data model
         * 2) run client_validation in metadata
         */
        if (metadataValidation) {
          if (validatorStr) {
            validatorStr = validatorStr + ';' + metadataValidation;
          } else {
            validatorStr = metadataValidation;
          }
        }

        if (validatorStr) {
          const validatorStrArray: Array<any> = validatorStr.split(';');
          const promises = [];
          validatorStrArray.forEach((validator) => {
            promises.push(this.validationService.runValidator(validator, value));
          });
          isValid = (await Promise.all(promises)).every((res: ValidationResult) => {
            result = res;
            return result && result.result;
          });
        }
      }

      if (isValid && propertyDef.compositeValidators) {
        const promises = [];
        propertyDef.compositeValidators.every((validatorFn) => {
          if (!this.validationService[validatorFn]) {
            const error = {
              isHumanRelated: false,
              data: {
                validatorFn,
              },
            };
            UILoggerSingleton.getInstance().error({
              module: 'application.service.ts',
              function: 'runBusinessValidation',
              category: UICategory.request,
              error: error,
            });
            return false;
          } else {
            promises.push(this.validationService[validatorFn](this.controller.customer, property, value));
            return true;
          }
        });
        (await Promise.all(promises)).every((res: ValidationResult) => {
          result = res;
          return result && result.result;
        });
      }
    }
    return result;
  }

  async runSectionValidation(sectionName: string) {
    const isExistingPoliciesSection = sectionName === 'Existing Policies';
    const isNewReplacementFeatureEnabled = this.windowService.nativeWindow.controller.enableNewReplacementFeature;
    const isCas = this.appState.controller.customer.application.primaryPolicy.getChannelCode() === 'CAS';
    const isCoverpath2019MetadataEnabled =
      this.windowService.nativeWindow.controller.customer.isCoverpath2019MetadataEnabled();

    if (isExistingPoliciesSection && isNewReplacementFeatureEnabled && isCas && !isCoverpath2019MetadataEnabled) {
      // get existing policies to get values
      const existingPolicies = this.appState.controller.customer.application.primaryPolicy.insured.existingPolicies;

      // get section questions from question metadata
      const sectionQuestions = this.appState.appMetadata.children[7];

      // Application Level Replacement Question
      // get first question metadata
      const applicationLevelReplacementQuestion = sectionQuestions.children[0];
      // run validation passing in the prop, value, and options that are needed in the replacements rule
      await this.runBusinessValidation(
        applicationLevelReplacementQuestion.prop,
        existingPolicies.replaceInforceApplication,
        null,
        {
          prop: applicationLevelReplacementQuestion.prop,
          index: undefined,
          section: sectionName,
          appState: this.appState,
        }
      );

      // Existing Policy Level Qusetions
      const existingPolicyLevelQuestions = sectionQuestions.children[1].children[0];
      // go through the user added existing policies marked for replacement so their values can be passed in for validation
      existingPolicies.replacedOrFundedPolicies.forEach(async (existingPolicy, index) => {
        // need the index from existing policylist to get run validation correctly
        const correctIndex = existingPolicies.existingPolicyList.findIndex(
          (policy) => policy.number === existingPolicy.number && policy.companyName === existingPolicy.companyName
        );

        // there are 8 questions for adding an existing policy, we need the props for the last two from the question metadata:
        // Existing Policy Level Replacement Question, Existing Policy Level Finance Question
        // since there can be multiple existing policies, we need the prop to reflect the iteration
        let isReplacementProp = existingPolicyLevelQuestions.children[6].prop;
        isReplacementProp = isReplacementProp.replace('[i]', '[' + correctIndex + ']');
        let isReplacementFundsProp = existingPolicyLevelQuestions.children[7].prop;
        isReplacementFundsProp = isReplacementFundsProp.replace('[i]', '[' + correctIndex + ']');

        // run validation for both questions passing in the prop, value, and options that are needed in the replacements rule
        await this.runBusinessValidation(isReplacementProp, existingPolicy.isReplacement, null, {
          prop: isReplacementProp,
          index: correctIndex,
          section: sectionName,
          appState: this.appState,
        });
        await this.runBusinessValidation(isReplacementFundsProp, existingPolicy.isReplacementFunds, null, {
          prop: isReplacementFundsProp,
          index: correctIndex,
          section: sectionName,
          appState: this.appState,
        });
      });
    }
  }

  getSectionLevelNodes(): CasMetadataNode[] {
    return this.appState.appMetadata.children;
  }

  getSectionLevelNodeByIndex(sectionIndex: number): CasMetadataNode {
    if (sectionIndex < 0 || sectionIndex >= this.appState.appMetadata.children.length) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'application.service.ts',
          function: 'getSectionLevelNodeByIndex',
          category: UICategory.request,
        },
        'try to go to a not existing section'
      );
      return <any>{};
    } else {
      return this.getSectionLevelNodes()[sectionIndex];
    }
  }

  getSectionQuestions(sectionIndex: number, exploreCheckboxNodes?: boolean): CasMetadataNode[] {
    const sectionNode = this.getSectionLevelNodeByIndex(sectionIndex);

    if (ld.isEmpty(sectionNode) || !('children' in sectionNode)) {
      return [];
    } else {
      try {
        const sectionQuestions: CasMetadataNode[] = [];
        const allQuestions: CasMetadataNode[] = []; // Queue containing all questions for traversal
        allQuestions.push(...sectionNode.children);

        /**
         * Perform a breadth-first search down the metadata tree, adding all nodes to this
         * list of section questions for tracking progress
         */
        while (allQuestions.length > 0) {
          const q = allQuestions.shift();

          if (!q.optional && q.id !== '-1') {
            sectionQuestions.push(q);
          }

          /**
           * All items of an iterate-collection all always point to the same metadata node,
           * this creates some complications for tracking their progress, so we need to
           * create additional un-referenced nodes to accurately track and validate.
           *
           * Additionally, the loopIndex is created during a UI paradigm, creating
           * extra complications.
           *
           * When we instantiate a child of an iterate-collection, we set it's underlying
           * node's loopIndex equal to the new maximum index of that collection.
           *
           * When we see one of those nodes here, we want to add additional nodes
           * with indexes from 0 up to that maximum index. We'll track these for progress.
           */
          if (!isNaN(q.loopIndex)) {
            for (let i = q.loopIndex - 1; i >= 0; i--) {
              const newQ: CasMetadataNode = q.shallowCopy();
              newQ.loopIndex = i;
              sectionQuestions.push(newQ);
            }
          } else if (q.parent.controlType === 'iterate-collection') {
            /**
             * This case occurs when there is an iterate-collection open somewhere in the app
             * that has not been instantiated in UI yet. At this point we only have the default,
             * first set of children, and we can set their loopIndex to 0.
             */
            q.loopIndex = 0;
          }

          if (exploreCheckboxNodes) {
            allQuestions.push(...q.children.filter((q) => q.isShown()));
          } else {
            // Validating checkboxes is a bit more complicated so it is skipped by default
            allQuestions.push(...q.children.filter((q) => q.controlType !== 'checkbox-basic'));
          }
        }

        return sectionQuestions;
      } catch (error) {
        /**
         * Something went wrong when recursing down, log an error
         * and return just the top level nodes instead, using only
         * the most basic filter
         */

        this.amorphicService.amorphicLogError(
          'application.service',
          'getSectionQuestions',
          error,
          'Recursing down the tree of section questions failed'
        );
        return sectionNode.children.filter((q) => !q.optional && q.id !== '-1');
      }
    }
  }

  getShownQuestions(questions: CasMetadataNode[]): CasMetadataNode[] {
    return questions.filter((q) => q.isShown(q.loopIndex));
  }

  getShownAnsweredSectionQuestions(sectionIndex: number): CasMetadataNode[] {
    const answeredSectionQuestions = this.getAnsweredSectionQuestions(sectionIndex);
    return this.getShownQuestions(answeredSectionQuestions);
  }

  getShownSectionQuestions(sectionIndex: number): CasMetadataNode[] {
    const sectionQuestions = this.getSectionQuestions(sectionIndex);
    return this.getShownQuestions(sectionQuestions);
  }

  isQuestionLockedSection(sectionIndex: number): boolean {
    return !!this.getShownSectionQuestions(sectionIndex).find((obj) => obj.isQuestionLockable === true);
  }

  getAnsweredSectionQuestions(sectionIndex: number): CasMetadataNode[] {
    const sectionQuestions: CasMetadataNode[] = this.getSectionQuestions(sectionIndex);
    return sectionQuestions.filter((q) => q.isValid(q.loopIndex));
  }

  getInvalidSectionQuestions(sectionIndex: number): CasMetadataNode[] {
    const sectionQuestions: CasMetadataNode[] = this.getSectionQuestions(sectionIndex, true);

    // Return array of invalid questions
    return sectionQuestions.filter((q) => !q.isValid(q.loopIndex));
  }

  getPreceedingInvalidProps(sectionIndex: number, questionProp: string): string[] {
    const sectionQuestionProps: string[] = this.appState.sectionPropStrings[sectionIndex].slice();
    const currentQuestionPropEl: number = sectionQuestionProps.indexOf(questionProp);
    const invalidQuestionProps: string[] = [];

    // Create an array of propStrings for each invalid question node
    this.getInvalidSectionQuestions(sectionIndex).forEach((question: CasMetadataNode) => {
      invalidQuestionProps.push(...this.extractSubPropStrings(question));
    });

    // return the array of invalid props that are before this question
    return invalidQuestionProps.reduce((strings, invalidProp) => {
      if (sectionQuestionProps.findIndex((prop: string) => prop === invalidProp) < currentQuestionPropEl) {
        strings.push(invalidProp);
      }
      return strings;
    }, []);
  }

  extractSubPropStrings(node: CasMetadataNode): string[] {
    const propStrings: string[] = [];

    // Custom components represent one node but may have several props.
    // ex. a custom-address-long component should be extracted to show street,line1,zip,city,state
    if (node.customProps) {
      node.customProps.forEach((customProp) => propStrings.push(`${node.id}_${node.prop}.${customProp}`));
      // Similarly for child nodes in DiagnosisShortNode
    } else if (node instanceof DiagnosisShortNode) {
      node.children.forEach((child) => propStrings.push(`${child.id}_${child.prop}`));
    } else {
      propStrings.push(`${node.id}_${node.prop}`);
    }

    return propStrings;
  }

  async sendAAApplicationToClient(): Promise<{ application: CasApplication; isAdvisorEdit: boolean }> {
    if (this.appState.isAdvisor) {
      this.sendButtonDisabledSubject.next(true);
      const controller = (window as any).controller as any;
      const shareLinkService: ShareLinkService = this.injector.get(ShareLinkService);
      const modalService: ModalService = this.injector.get(ModalService);
      modalService.activate(AdvisorProcessingApplication);
      // Importing ApplicationSourceType from Application is causing issues with hit-snap-svc
      const isAdvisorEdit = this.amorphicService.controller.customer.application.sourceType === 'advisorEdit';
      try {
        const success = await controller.applicationSubmissionController.submitAAApplication();
        if (success) {
          await shareLinkService.generateAdvisorAssistantApplicationLink(isAdvisorEdit);
          await controller.applicationLockController.setApplicationLockedBy(LockedByUserType.CUSTOMER);
          const modalType = {
            type: ModalType.previewInvitation,
            extraClasses: 'modal-preview-mailto-alt',
            inviteType: isAdvisorEdit ? InviteEmailType.ADVISOR_EDIT : InviteEmailType.ADVISOR_ASSISTED,
            exitRoute: '/account-center',
          };
          modalService.activate(modalType);
        } else {
          modalService.activate(AdvisorSubmitFailedConfig);
        }
      } catch (error) {
        modalService.activate(AdvisorSubmitFailedConfig);
        this.amorphicService.amorphicLogError(
          'application.service',
          'sendAAApplicationToClient',
          error,
          'Error when submitting application from call to controller.applicationSubmissionController.submitAAApplication'
        );
      }
      return { application: this.amorphicService.controller.customer.application as CasApplication, isAdvisorEdit };
    }
  }

  async submitFromService(): Promise<void | { code: string; message: string }> {
    this.advisorSubmittedApplicationSentSubject.next(true);
    const controller = this.amorphicService.controller;
    // Don't update anything if it's LifeBridge to avoid overwriting hardcoded values
    if (!controller.customer.application.primaryPolicy.isLifeBridge()) {
      this.updateDependentApplicationValues();
    }
    await controller.prepareForPolicySubmission();
    const error = await controller.applicationController.submitPolicy(controller.customer);
    return error;
  }

  async resendEmailVerification(): Promise<void> {
    if (!this.appState.isAdvisor) {
      const accountCenterService: AccountCenterService = this.injector.get(AccountCenterService);
      await accountCenterService.resendEmailVerification();
    }
  }

  async updateAssociatedProperties(prop: string, value: string): Promise<void> {
    let modifiedPrimary = false;
    let modifiedSecondary = false;

    if (
      prop === 'customer.application.primaryPolicy.insured.person.dob' ||
      prop === 'customer.application.primaryPolicy.insured.person.gender' ||
      prop === 'customer.application.primaryPolicy.insured.address.state' ||
      prop === 'customer.application.primaryPolicy.paymentFrequency' ||
      prop === 'customer.application.primaryPolicy.waiver' ||
      prop === 'customer.application.primaryPolicy.term' ||
      prop === 'customer.application.primaryPolicy.faceAmount' ||
      prop === 'customer.application.primaryPolicy.termDisplay' ||
      prop === 'customer.application.primaryPolicy.hasEcp' ||
      prop === 'customer.application.primaryPolicy.dividendOption' ||
      prop === 'customer.application.primaryPolicy.loanInterestType' ||
      prop === 'customer.application.primaryPolicy.hasAlirRider' ||
      prop === 'customer.application.primaryPolicy.additionalLifeInsuranceAmount'
    ) {
      modifiedPrimary = true;
    } else if (
      prop === 'customer.application.secondaryPolicy.waiver' ||
      prop === 'customer.application.secondaryPolicy.term' ||
      prop === 'customer.application.secondaryPolicy.faceAmount' ||
      prop === 'customer.application.secondaryPolicy.termDisplay' ||
      prop === 'customer.application.secondaryPolicy.hasEcp'
    ) {
      modifiedSecondary = true;
    }

    // if riders change
    modifiedPrimary =
      modifiedPrimary ||
      prop === 'customer.application.primaryPolicy.hasGirRider' ||
      prop === 'customer.application.primaryPolicy.hasGirRiderBool' ||
      prop === 'customer.application.primaryPolicy.girRiderAmount' ||
      prop === 'customer.application.primaryPolicy.hasRtrRider' ||
      prop === 'customer.application.primaryPolicy.hasRtrRiderBool' ||
      prop === 'customer.application.primaryPolicy.rtrRiderAmount' ||
      prop === 'customer.application.primaryPolicy.hasCirRider' ||
      prop === 'customer.application.primaryPolicy.cirRiderAmount' ||
      prop === 'customer.application.primaryPolicy.benefitDisbursementMethod';

    // this logic was originally here as a band-aid from selectedQuote era but should be refactored.
    modifiedSecondary = modifiedSecondary || !!this.controller.customer.application.secondaryPolicy;

    if (prop.indexOf('term') !== -1 || prop.indexOf('faceAmount') !== -1 || prop.indexOf('hasEcp') !== -1) {
      SubjectManager.publish('update_faceterm');
    }

    const simpleProp = prop.split('.').slice(-1).join();

    /**
     * Only certain application changes affects the waiver of premium amount
     * TODO where to put list of properties that affect waiver?
     */
    if (PropertiesForWoP.includes(simpleProp)) {
      if (modifiedSecondary) {
        await ApplicationDataService.updateWOPAndPremium(
          this.controller,
          <CoverpathBasePolicy>this.controller.customer.application.secondaryPolicy
        );
      }

      if (modifiedPrimary) {
        await ApplicationDataService.updateWOPAndPremium(
          this.controller,
          <CoverpathBasePolicy>this.controller.customer.application.primaryPolicy
        );
      }
    }

    /**
     * Only certain application changes affects the ECP convertibleMaxYears value
     */
    if (PropertiesForECP.includes(simpleProp)) {
      if (modifiedSecondary) {
        await ApplicationDataService.updateConvertibleMaxYears(
          this.controller,
          <CoverpathBasePolicy>this.controller.customer.application.secondaryPolicy
        );
      }

      if (modifiedPrimary) {
        await ApplicationDataService.updateConvertibleMaxYears(
          this.controller,
          <CoverpathBasePolicy>this.controller.customer.application.primaryPolicy
        );
      }
    }

    return Promise.resolve();
  }

  /**
   * Handle edge case where an applicant starts in a state that supports secondary addressees and indicates they want
   * to provide one, but then updates their state to one where secondary addressees are not supported.
   */
  checkSecondaryAddresseeStatus(): void {
    let policy: CoverpathPolicy | MMDirectPolicy;

    if (this.controller.customer.application.primaryPolicy.getChannelCode() === 'CAS') {
      policy = this.controller.customer.application.primaryPolicy as CoverpathPolicy;
    } else if (this.controller.customer.application.primaryPolicy.getChannelCode() === 'MMDirect') {
      policy = this.controller.customer.application.primaryPolicy as MMDirectPolicy;
    } else {
      return;
    }

    if (!policy.isSecondaryAddresseeState(this.controller.customer) && policy.hasSecondaryAddressee) {
      //reset policy to how it started
      policy.hasSecondaryAddressee = undefined;
      policy.initSecondaryAddresseePerson(this.controller.customer);
    }
  }

  // One way syncing from quote to app - for generic link
  updateApplicationValues(quote: BaseQuote): void {
    const policy = this.controller.customer.application
      ? this.controller.customer.application.primaryPolicy
      : undefined;

    if (!policy || !policy.insured) {
      return;
    }

    const applicant = policy.insured;
    const applicantAddress = policy.isOwnerSameAsInsured()
      ? policy.insured.address
      : policy.ownerPersonResidentialAddress;

    applicant.person.gender = quote.gender ? quote.gender : applicant.person.gender;
    applicant.person.dob = quote.dateOfBirth ? quote.dateOfBirth : applicant.person.dob;

    // Juvenile family member questions have no Metadata trigger and are at the root, so they must be triggered manually
    applicant.person.familyMembersTrigger();

    applicantAddress.zip =
      quote.contractState && quote.contractState !== applicantAddress.state ? undefined : applicantAddress.zip;
    applicantAddress.city =
      quote.contractState && quote.contractState !== applicantAddress.state ? undefined : applicantAddress.city;
    applicantAddress.state =
      quote.contractState && quote.contractState !== applicantAddress.state
        ? quote.contractState
        : applicantAddress.state;

    policy.faceAmount = quote.faceAmount;
    policy.term = quote.term;
    policy.waiver = !!quote.waiverOfPremium;
    policy.product = quote.product;
    (policy as CoverpathBasePolicy).hasEcp =
      quote.product === Product.products.CASTermECP.code || quote.product === Product.products.MMDirectTermECP.code
        ? true
        : false;
    policy.paymentFrequency = quote.paymentFrequency.toLowerCase();

    if (this.controller.isTeleCMIEnabled) {
      this.controller.customer.application.isTeleCMI = policy.isTeleCMIRequired();
    }
  }
  /**
   * Aggregate function to call other functions synchronize and update values on submittion of application.
   */
  updateDependentApplicationValues(): void {
    this.syncOwnersName();
  }

  /**
   * If we update the insurer's name in the application and that is who the owner of the policy will be,
   * let's update the owners name to match the insurer's.
   */
  syncOwnersName(): void {
    const controller = this.amorphicService.controller;
    const customer = controller.customer;
    const policy = controller.customer.application.primaryPolicy;
    if (policy.ownerType === 'you') {
      const insured = policy.insured.person;
      customer.firstName = insured.firstName;
      customer.lastName = insured.lastName;
    }
  }

  isAuth0Enabled(): boolean {
    return this.windowService.nativeWindow.controller.isAuth0Enabled;
  }

  async recordCoverpathActiveApps() {
    const controller = this.amorphicService.controller;

    if (controller.customer.isCoverpathCustomer()) {
      //record active multiple apps
      const customer: CoverpathCustomer = controller.customer as CoverpathCustomer;
      await customer.recordActiveApplications();
    }
  }

  async handleTeleInterviewerSubmit() {
    const modalType = {
      type: ModalType.teleInterviewerSubmission,
    };
    const modalService: ModalService = this.injector.get(ModalService);
    modalService.activate(modalType);
    const subscription = modalService.userResponse.subscribe(async (dialogResponse) => {
      subscription.unsubscribe();
      if (dialogResponse && dialogResponse['proceed']) {
        this.sendButtonDisabledSubject.next(true);
        await (this.controller as AgentController).applicationSubmissionController.completeTeleInterview(
          this.controller.customer.application as CasApplication
        );
        this.router.navigate([CoverpathURLs.ACCOUNT_CENTER], {
          queryParams: {
            hasTeleInterviewerSubmitted: 'true',
            insuredName: this.controller.customer.application.primaryPolicy.insured.person.firstNameLastName,
          },
        });
      }
    });
  }
}
